import React, { useState, useEffect, useContext } from 'react';
import FormControl from '@mui/material/FormControl';
import {
  Button,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Grid,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { createApiClient } from 'api/apiClient';
import { useQuery, useMutation } from 'react-query';
import { UserContext } from 'contexts/userContext';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { BarChart as BarChartIcon, Refresh as RefreshIcon, Close as CloseIcon } from '@mui/icons-material';

function RPMDeviceOrdersHistory() {
  const { userData } = useContext(UserContext);
  const accessToken = localStorage.getItem('access_token');
  const controller = new AbortController();

  const [orderHistoryData, setOrderHistoryData] = useState<any[]>([]);
  const [orderHistoryDataLength, setOrderHistoryDataLength] =
    useState<number>(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('order_datetime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedReminingDevice, setSelectedRemainingDevice] =
    useState<string>('Blood Pressure');
  const [providerOrg, setProviderOrg] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);

  const OrgID = userData?.userOrg;

  const apiClient = createApiClient(accessToken!, controller);

  const {
    isLoading,
    refetch: refresh,
    data,
  } = useQuery('get-rpm-device-order-history', () =>
    apiClient.getRpmDeviceOrderHistory(OrgID as number),
  );

  const {
    isLoading: isLoadingRemainingDevice,
    refetch: refetchRemainingDevice,
    data: remainingDeviceData,
  } = useQuery('get-remaining-device-data', () =>
    apiClient.getRemainingDeviceCount(),
  );

  useEffect(() => {
    setOrderHistoryData(data);
  }, [data]);

  useEffect(() => {
    setOrderHistoryDataLength(orderHistoryData?.length);
  }, [orderHistoryData]);

  useEffect(() => {
    if (data) {
      const sortedData = [...data].sort((a, b) => {
        if (sortOrder === 'asc') {
          return new Date(a[sortBy]).getTime() - new Date(b[sortBy]).getTime();
        } else {
          return new Date(b[sortBy]).getTime() - new Date(a[sortBy]).getTime();
        }
      });
      setOrderHistoryData(sortedData);
      setOrderHistoryDataLength(sortedData.length);
    }
  }, [data, sortBy, sortOrder]);

  const columns = [
    { id: 'Sr', label: 'Sr. No.', minWidth: 17 },
    { id: 'dt', label: 'Device Type', minWidth: 170 },
    { id: 'pn', label: 'Vendor Name', minWidth: 170 },
    { id: 'ob', label: 'Ordered By', minWidth: 170 },
    { id: 'qt', label: 'Quantity', minWidth: 170 },
    { id: 'od', label: 'Order Date', minWidth: 170 },
  ];

  const Refresh = withStyles({
    root: {
      margin: '10px',
    },
    label: {
      textTransform: 'capitalize',
    },
  })(Button);

  const handleRefresh = () => {
    refresh();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSort = (column: string) => {
    if (column === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  // Group orderHistoryData by provider and device type
  const getSummaryData = () => {
    const summary = {};
    orderHistoryData?.forEach(order => {
      const provider = order.device_provider_name || 'Unknown Provider';
      const deviceType = order.device_type || 'Unknown Device';
      if (!summary[provider]) summary[provider] = {};
      if (!summary[provider][deviceType]) summary[provider][deviceType] = 0;
      summary[provider][deviceType] += Number(order.quantity) || 0;
    });
    return summary;
  };
  const summaryData = getSummaryData();

  // Device type icons
  const deviceTypeIcon = type => {
    if (type.toLowerCase().includes('blood')) return '🩺';
    if (type.toLowerCase().includes('weigh')) return '⚖️';
    return '🔹';
  };

  return (
    <div style={{marginTop: -85}}>
      {!isLoading ? (
        <>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fff', borderRadius: 2, width: 265 }}>
                <InputLabel shrink htmlFor="provider-org-select" sx={{ color: '#888', fontWeight: 400 }}>
                  Select Device
                </InputLabel>
                <Select
                  labelId="provider-org-label"
                  id="provider-org-select"
                  value={selectedReminingDevice}
                  onChange={e => setSelectedRemainingDevice(e.target.value)}
                  label="Select Device"
                  renderValue={selected => selected ? selected : <span style={{ color: '#bbb' }}>Select Provider</span>}
                  displayEmpty
                  sx={{ borderRadius: 2, minHeight: 48, '.MuiSelect-select': { py: 1 } }}
                >
                  <MenuItem value=""><em>Select Provider</em></MenuItem>
                  {remainingDeviceData && Object.entries(remainingDeviceData).map(([name, value], index) => (
                    <MenuItem key={index} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 400, color: '#222', mr: 1, mt: 0 }}>
                Remaining Device:
              </Typography>
              <Box
                sx={{
                  bgcolor: remainingDeviceData?.[selectedReminingDevice] > 0 ? '#eafaf1' : '#FCEAEA',
                  color: remainingDeviceData?.[selectedReminingDevice] > 0 ? '#3bb77e' : '#B93B3B',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 16,
                  display: 'inline-block',
                  mt: 0,
                  borderColor: remainingDeviceData?.[selectedReminingDevice] > 0 ? 'red' : 'red',
                  borderWidth: 2
                }}
              >
                {remainingDeviceData?.[selectedReminingDevice]}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {/* <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  minWidth: 200,
                  color: '#2196f3',
                  borderColor: '#2196f3',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#e3f2fd', borderColor: '#2196f3' },
                  mt: 0,
                  mr: -12
                }}
                onClick={() => setSummaryOpen(true)}
              >
                Device Order Summary
              </Button> */}
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Box>
                <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                  <Select value={sortOrder} onChange={handleSortOrderChange}>
                    <MenuItem value="desc">New to Old</MenuItem>
                    <MenuItem value="asc">Old to New</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  color="primary"
                  onClick={handleRefresh}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#e3f2fd' },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          <Paper sx={{ width: '100%', overflow: 'hidden', mt: 5 }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column:any) => (
                      <TableCell
                        key={column.id}
                        align={'center'}
                        style={{ minWidth: column.minWidth }}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                        {sortBy === column.id && (
                          <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {orderHistoryData
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    .map((order, index) => {
                      return (
                        <TableRow
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? '#ddeffd' : 'inherit',
                          }}
                        >
                          <TableCell
                            key={'sn'}
                            component="th"
                            scope="col"
                            align={'center'}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell key={'dt'} align={'center'}>
                            {order?.device_type}
                          </TableCell>
                          <TableCell key={'pn'} align={'center'}>
                            {order?.device_provider_name}
                          </TableCell>
                          <TableCell key={'ob'} align={'center'}>
                            {order?.order_by}
                          </TableCell>
                          <TableCell key={'qu'} align={'center'}>
                            {order?.quantity}
                          </TableCell>
                          <TableCell key={'od'} align={'center'}>
                            {order
                              ? new Date(
                                  order.order_datetime,
                                ).toLocaleDateString()
                              : ''}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component={'div'}
            count={orderHistoryDataLength}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelRowsPerPage=""
            rowsPerPageOptions={[]}
          />

          {/* Device Order Summary Dialog */}
          <Dialog
            open={summaryOpen}
            onClose={() => setSummaryOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                bgcolor: '#fff',
                boxShadow: 'none',
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: '#90c2ee',
                color: '#ffff',
                fontWeight: 600,
                fontSize: 18,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                px: 4,
                pt: 1,
                pb: 1,
                minHeight: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              Device Order Summary
              <IconButton
                aria-label="close"
                onClick={() => setSummaryOpen(false)}
                sx={{ color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                bgcolor: '#ffff',
                px: 0,
                py: 2,                             
                maxHeight: 500,
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              }}
            >
              <Stack spacing={3} sx={{ px: 2, mt: 2 }}>
                {Object.entries(summaryData).map(([provider, devices]) => (
                  <Box
                    key={provider}
                    sx={{
                      bgcolor: '#f6fbff',
                      borderRadius: 3,
                      p: 2,
                      mb: 1,
                      boxShadow: 0,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 1, color: '#222', fontSize: 18, marginRight: 14 }}>
                      {provider}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      {Object.entries(devices as Record<string, number>).map(
                        ([type, count]) => (
                          <Box
                            key={type}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: '#fff',
                              borderRadius: 2,
                              p: 1.5,
                              minWidth: 180,
                              boxShadow: 0,
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography sx={{ fontSize: 26, mr: 2 }}>
                              {type.toLowerCase().includes('blood') ? '🩺' : type.toLowerCase().includes('weigh') ? '⚖️' : '🔹'}
                            </Typography>
                            <Typography sx={{ flex: 1, fontWeight: 500, color: '#222', fontSize: 14 }}>
                              {type}
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: '#e0f0ff',
                                color: '#2196f3',
                                borderRadius: 2,
                                px: 2,
                                py: 0.5,
                                fontWeight: 700,
                                fontSize: 18,
                                minWidth: 38,
                                textAlign: 'center',
                                ml: 1,
                              }}
                            >
                              {String(count).padStart(2, '0')}
                            </Box>
                          </Box>
                        ),
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        >
          <CircularProgress color="primary" />
        </div>
      )}
    </div>
  );
}

export default RPMDeviceOrdersHistory;
